import React from 'react';

import { Grid, Stack, Typography } from '@mui/material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from 'recharts';
import { ButtonWithIcon } from 'src/v2/components/ui/buttons/buttonWithIcon';

import { primaryColors, secondaryColors } from '~components/theme/styles/colors';
import { label3 } from '~components/theme/typography';

import SlideWrapper from '../SlideWrapper';
interface VRRPegGraphData {
  depeg: number;
  vrrHigh: number;
  vrrMed: number;
  vrrLow: number;
}

function linspace(start: number, stop: number, iterations: number, endpoint = true) {
  const div = endpoint ? iterations - 1 : iterations;
  const step = (stop - start) / div;
  return Array.from({ length: iterations }, (_, i) => start + step * i);
}

function generateVrrCurveDataPoints(iterations: number) {
  const xRange = linspace(-10, 10, iterations);
  const vrrConstant = -1.0397;
  const vrrHighBaseRate = 1.25;
  const vrrMedBaseRate = 0.75;
  const vrrLowBaseRate = 0.5;

  const vrrHighMax = 226.250672;
  const vrrMedMax = 135.750403;
  const vrrLowMax = 90.500269;

  return xRange.map(xDepegVal => ({
    depeg: xDepegVal,
    vrrHigh: Math.min(vrrHighMax, vrrHighBaseRate * Math.exp(vrrConstant * xDepegVal)),
    vrrMed: Math.min(vrrMedMax, vrrMedBaseRate * Math.exp(vrrConstant * xDepegVal)),
    vrrLow: Math.min(vrrLowMax, vrrLowBaseRate * Math.exp(vrrConstant * xDepegVal)),
  })) as VRRPegGraphData[];
}

const data = generateVrrCurveDataPoints(500);

const VRRPegGraph = () => {
  return (
    <ResponsiveContainer width="99%" height={300}>
      <LineChart
        data={data}
        margin={{
          top: 5,
          right: 10,
          left: 20,
          bottom: 5,
        }}
      >
        <CartesianGrid stroke={primaryColors[900]} strokeDasharray="3 3" />
        <XAxis
          style={{ ...label3, fill: primaryColors[400] }}
          label={<Typography>PRICE PEG</Typography>}
          ticks={[-10, -8, -6, -4, -2, 0, 2, 4, 6, 8, 10]}
          tickFormatter={val => `${val}%`}
          dataKey="depeg"
          axisLine={false}
        />
        <YAxis
          style={{ ...label3, fill: primaryColors[400] }}
          axisLine={false}
          label={<Typography>VRR</Typography>}
          tickSize={0}
          tickFormatter={val => `${val}%`}
        />
        <Line type={'monotone'} dataKey="vrrHigh" stroke={secondaryColors[500]} dot={false} />
        <Line type="monotone" dataKey="vrrMed" stroke={secondaryColors[300]} dot={false} />
        <Line type="monotone" dataKey="vrrLow" stroke={secondaryColors[100]} dot={false} />
      </LineChart>
    </ResponsiveContainer>
  );
};

function PegSlide() {
  return (
    <SlideWrapper
      sx={{
        justifyContent: 'center',
        overflow: 'hidden',
      }}
    >
      <Grid
        container
        direction={{ xs: 'column-reverse', sm: 'row' }}
        textAlign={{ xs: 'center', sm: 'start' }}
        spacing={4}
      >
        <Grid item xs={12} sm={5.5}>
          <Stack spacing={4}>
            <Typography variant={'h4'}>Pegged to $1</Typography>
            <Stack spacing={2}>
              <Typography variant={'h6'} color={primaryColors[100]}>
                Price Floor enabled by Vesta Reference Rate
              </Typography>
              <Typography variant={'body2'} color={primaryColors[400]}>
                Rate goes up as token depegs, encouraging supply reduction
              </Typography>
            </Stack>

            <Stack spacing={2}>
              <Typography variant={'h6'} color={primaryColors[100]}>
                Price Ceiling enabled by low liquidation ratio
              </Typography>
              <Typography variant={'body2'} color={primaryColors[400]}>
                If the stablecoin goes above $1.1, people could mint VST using ETH and sell VST into the open
                market to realize an arbitrage
              </Typography>
            </Stack>

            <ButtonWithIcon iconType="asterisk"> Learn More </ButtonWithIcon>
          </Stack>
        </Grid>
        <Grid item xs={12} sm={5.5}>
          <VRRPegGraph />
        </Grid>
      </Grid>
    </SlideWrapper>
  );
}

export default PegSlide;
